import React, { useEffect, useState } from "react";
import { api, fmtINR, fmtDate, today } from "@/lib/api";
import { PageHeader, Card, Empty } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  TrendingUp, BarChart3, PieChart, Wallet, ArrowUpRight, ArrowDownRight,
  TrendingDown, Package, Users, ShoppingCart, Activity, ShieldAlert, CheckCircle2,
  Truck, Clock, AlertTriangle, Layers, Percent
} from "lucide-react";

function defaultFromDate() {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.toISOString().slice(0, 10);
}

export default function Analytics() {
  const [frm, setFrm] = useState(defaultFromDate());
  const [to, setTo] = useState(today());
  const [loading, setLoading] = useState(false);

  const [advData, setAdvData] = useState(null);
  const [salesData, setSalesData] = useState([]);
  const [productData, setProductData] = useState([]);
  const [routeData, setRouteData] = useState([]);
  const [driverData, setDriverData] = useState([]);

  async function loadData() {
    setLoading(true);
    try {
      const [advRes, salesRes, prodRes, routeRes, driverRes] = await Promise.all([
        api.get("/analytics/advanced", { params: { frm, to } }),
        api.get("/reports/sales", { params: { frm, to } }),
        api.get("/reports/products", { params: { frm, to } }),
        api.get("/deliveries/reports/by-route", { params: { frm, to } }),
        api.get("/deliveries/reports/by-driver", { params: { frm, to } }),
      ]);

      setAdvData(advRes.data || null);
      setSalesData(salesRes.data || []);
      setProductData(prodRes.data || []);
      setRouteData(routeRes.data || []);
      setDriverData(driverRes.data || []);
    } catch (err) {
      console.error("Failed to load advanced analytics:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, []);

  const summary = advData?.summary || {};
  const deliveryPerf = advData?.delivery_performance || {};
  const dailyTrends = advData?.daily_trends || [];
  const paymentModes = advData?.payment_modes || {};
  const orderStatuses = advData?.order_statuses || {};
  const topDebtors = advData?.top_debtors || [];

  const maxRevenueTrend = dailyTrends.length > 0 ? Math.max(...dailyTrends.map(d => d.revenue), 1) : 1;

  return (
    <div className="space-y-6 pb-12" data-testid="analytics-page">
      <PageHeader
        eyebrow="Enterprise Business Intelligence"
        title="Advanced Operations & Financial Analytics"
        description="High-level dairy distribution intelligence dashboard with logistics KPIs, cash-flow splits, receivables aging, and demand forecasting."
      />

      {/* Date Filter & Performance Header */}
      <Card padding="p-4">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-1">
                From Date
              </label>
              <Input
                type="date"
                value={frm}
                onChange={(e) => setFrm(e.target.value)}
                className="bg-white font-mono text-sm"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.18em] text-slate-500 font-semibold block mb-1">
                To Date
              </label>
              <Input
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="bg-white font-mono text-sm"
              />
            </div>
            <Button
              onClick={loadData}
              disabled={loading}
              className="bg-cobalt-600 hover:bg-cobalt-700 text-white font-medium"
            >
              {loading ? "Recalculating..." : "Apply Period Filter"}
            </Button>
          </div>

          <div className="flex items-center gap-2 bg-slate-100/80 px-3.5 py-2 rounded-lg border border-slate-200 text-xs font-mono text-slate-700">
            <Activity size={15} className="text-cobalt-600" />
            <span>Fulfillment Rate: <b>{summary.fulfillment_rate || 100}%</b></span>
          </div>
        </div>
      </Card>

      {/* SECTION 1: Industry Executive KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card padding="p-5" className="border-l-4 border-l-cobalt-600">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Gross Sales Revenue</span>
            <div className="w-8 h-8 rounded-full bg-cobalt-50 text-cobalt-600 flex items-center justify-center">
              <TrendingUp size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900">{fmtINR(summary.total_revenue || 0)}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>Avg Order Value:</span>
            <b className="font-mono text-slate-700">{fmtINR(summary.avg_order_value || 0)}</b>
          </div>
        </Card>

        <Card padding="p-5" className="border-l-4 border-l-emerald-500">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Cash Collections</span>
            <div className="w-8 h-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
              <Wallet size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900">{fmtINR(summary.total_collections || 0)}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>Payments Ingested:</span>
            <b className="text-emerald-700 font-semibold">{Object.keys(paymentModes).length} Modes</b>
          </div>
        </Card>

        <Card padding="p-5" className="border-l-4 border-l-amber-500">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Total Receivables</span>
            <div className="w-8 h-8 rounded-full bg-amber-50 text-amber-600 flex items-center justify-center">
              <AlertTriangle size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900">{fmtINR(summary.total_outstanding || 0)}</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>Top Debtors Count:</span>
            <b className="text-amber-700">{topDebtors.length} Customers</b>
          </div>
        </Card>

        <Card padding="p-5" className="border-l-4 border-l-indigo-500">
          <div className="flex justify-between items-start mb-2">
            <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">Repeat Retention</span>
            <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <Percent size={18} />
            </div>
          </div>
          <div className="text-2xl font-bold font-heading text-slate-900">{summary.repeat_customer_rate || 0}%</div>
          <div className="text-xs text-slate-500 mt-1 flex items-center justify-between">
            <span>Low Stock Alerts:</span>
            <b className="text-rose-600">{summary.low_stock_alert_count || 0} Items</b>
          </div>
        </Card>
      </div>

      {/* SECTION 2: Daily Sales Trend Chart Visualization */}
      <Card testid="card-sales-trend-chart">
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <BarChart3 size={18} className="text-cobalt-600" />
            <h3 className="font-heading font-semibold text-slate-900">Daily Revenue & Order Volume Trends</h3>
          </div>
          <span className="text-xs text-slate-500 font-mono">
            {dailyTrends.length} Active Days in Selected Period
          </span>
        </div>

        {dailyTrends.length === 0 ? (
          <div className="py-8"><Empty /></div>
        ) : (
          <div className="space-y-3 pt-2">
            <div className="grid grid-cols-1 gap-2">
              {dailyTrends.slice(-14).map((d, idx) => {
                const pct = Math.round((d.revenue / maxRevenueTrend) * 100);
                return (
                  <div key={idx} className="flex items-center gap-3 text-xs">
                    <span className="w-24 font-mono text-slate-600 shrink-0">{d.date}</span>
                    <div className="flex-1 bg-slate-100 rounded-full h-4 overflow-hidden relative">
                      <div
                        className="bg-gradient-to-r from-cobalt-500 to-emerald-500 h-full rounded-full transition-all duration-500"
                        style={{ width: `${Math.max(pct, 4)}%` }}
                      ></div>
                    </div>
                    <span className="w-24 font-semibold text-right font-mono text-slate-900 shrink-0">
                      {fmtINR(d.revenue)}
                    </span>
                    <span className="w-16 text-slate-500 text-right shrink-0">({d.orders} ord)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* SECTION 3: Logistics & Delivery Dispatch Efficiency */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Route Distribution Metrics */}
        <Card testid="card-route-analytics">
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Truck size={18} className="text-cobalt-600" />
              <h3 className="font-heading font-semibold text-slate-900">Route-wise Delivery Fulfillment</h3>
            </div>
            <span className="text-xs font-mono text-slate-500">{routeData.length} Routes</span>
          </div>

          {routeData.length === 0 ? (
            <div className="py-8"><Empty /></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-700 font-medium text-xs uppercase tracking-wider">
                    <th className="py-2.5 px-3">Route Name</th>
                    <th className="py-2.5 px-3 text-center">Total Dispatches</th>
                    <th className="py-2.5 px-3 text-center">Delivered</th>
                    <th className="py-2.5 px-3 text-right">Fulfillment %</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {routeData.map((r, idx) => {
                    const rate = r.total > 0 ? Math.round((r.delivered / r.total) * 100) : 100;
                    return (
                      <tr key={idx} className="hover:bg-slate-50/60 transition-colors">
                        <td className="py-2.5 px-3 font-medium text-slate-900">{r.route_name}</td>
                        <td className="py-2.5 px-3 text-center font-mono text-slate-700">{r.total}</td>
                        <td className="py-2.5 px-3 text-center font-mono text-emerald-700 font-semibold">{r.delivered}</td>
                        <td className="py-2.5 px-3 text-right">
                          <span className={`px-2 py-0.5 rounded text-xs font-semibold ${rate >= 90 ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                            {rate}%
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Payment Collection Breakdown */}
        <Card testid="card-payment-breakdown">
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Wallet size={18} className="text-cobalt-600" />
              <h3 className="font-heading font-semibold text-slate-900">Payment Modes Collection Split</h3>
            </div>
            <span className="text-xs font-mono text-slate-500">Channel Distribution</span>
          </div>

          {Object.keys(paymentModes).length === 0 ? (
            <div className="py-8"><Empty /></div>
          ) : (
            <div className="space-y-4 pt-1">
              {Object.entries(paymentModes).map(([mode, amt], idx) => {
                const pct = summary.total_collections > 0 ? Math.round((amt / summary.total_collections) * 100) : 0;
                return (
                  <div key={idx} className="space-y-1.5">
                    <div className="flex justify-between text-xs font-medium text-slate-800">
                      <span className="capitalize">{mode} Payment</span>
                      <span className="font-mono font-semibold">{fmtINR(amt)} ({pct}%)</span>
                    </div>
                    <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
                      <div
                        className="bg-cobalt-600 h-full rounded-full"
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* SECTION 4: Top Debtors & Receivables Risk Matrix */}
      <Card testid="card-top-debtors">
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <ShieldAlert size={18} className="text-amber-600" />
            <div>
              <h3 className="font-heading font-semibold text-slate-900">Highest Outstanding Receivables Risk</h3>
              <p className="text-xs text-slate-500">Customers with the highest unpaid balances requiring follow-up.</p>
            </div>
          </div>
          <span className="text-xs font-mono text-amber-700 bg-amber-50 px-2.5 py-1 rounded border border-amber-200">
            High Priority Ledger Items
          </span>
        </div>

        {topDebtors.length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-500">No overdue balances recorded.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50/70 text-slate-700 font-medium text-xs uppercase tracking-wider">
                  <th className="py-2.5 px-3">Customer Name</th>
                  <th className="py-2.5 px-3">Business Name</th>
                  <th className="py-2.5 px-3 font-mono">Mobile Number</th>
                  <th className="py-2.5 px-3 text-right">Outstanding Balance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {topDebtors.map((c) => (
                  <tr key={c.id} className="hover:bg-amber-50/40 transition-colors">
                    <td className="py-2.5 px-3 font-semibold text-slate-900">{c.customer_name || c.name}</td>
                    <td className="py-2.5 px-3 text-slate-700">{c.business_name || "—"}</td>
                    <td className="py-2.5 px-3 font-mono text-xs text-slate-600">{c.mobile || "—"}</td>
                    <td className="py-2.5 px-3 text-right font-mono font-bold text-rose-600">
                      {fmtINR(c.outstanding || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
